import { useEffect, useRef, useState } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { useUnreadCount } from '../hooks/useUnreadCount';
import { useMarkAsRead } from '../hooks/useMarkAsRead';
import { NotificationItem } from './NotificationItem';
import './NotificationCenter.css';

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { data: unreadCount = 0 } = useUnreadCount();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useNotifications();
  const { markOne, markAll } = useMarkAsRead();

  const notifications = data?.pages.flatMap((p) => p.notifications) ?? [];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const handleMarkAll = () => {
    markAll.mutate();
  };

  const handleMarkOne = (id: string) => {
    markOne.mutate(id);
  };

  return (
    <div className="notification-center">
      <button
        ref={buttonRef}
        className="notification-center__bell"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        type="button"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-center__badge" aria-hidden="true">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div ref={panelRef} className="notification-center__panel" role="dialog" aria-label="Notifications">
          <div className="notification-center__header">
            <span className="notification-center__title">Notifications</span>
            {unreadCount > 0 && (
              <button
                className="notification-center__mark-all"
                onClick={handleMarkAll}
                type="button"
                disabled={markAll.isPending}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-center__list">
            {notifications.length === 0 ? (
              <p className="notification-center__empty">No notifications yet</p>
            ) : (
              <>
                {notifications.map((n) => (
                  <NotificationItem key={n.id} notification={n} onMarkRead={handleMarkOne} />
                ))}
                {hasNextPage && (
                  <button
                    className="notification-center__load-more"
                    onClick={() => void fetchNextPage()}
                    type="button"
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
